import React, { useEffect, useState } from 'react';
import { BUSINESS_CALENDAR_URL, getFetchRequest, postFetchRequest } from '../store/requests';
import { makeUrl, errorCallback } from '../constant';

const CALENDAR_RU = 'RU';
const CALENDAR_CN = 'CN';

const formatDateRu = (isoString) => {
    if (!isoString) return 'нет данных';
    try {
        const d = new Date(isoString);
        if (Number.isNaN(d.getTime())) return isoString;
        return d.toLocaleDateString('ru-RU');
    } catch {
        return isoString;
    }
};

const BusinessCalendarTab = ({ isCollapsed }) => {
    const [calendarCode, setCalendarCode] = useState(CALENDAR_RU);
    const [validUntil, setValidUntil] = useState(null);
    const [years, setYears] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [testBaseDate, setTestBaseDate] = useState('');
    const [testDays, setTestDays] = useState('');
    const [testResult, setTestResult] = useState(null);
    const [testError, setTestError] = useState(null);
    const [isTestLoading, setIsTestLoading] = useState(false);
    const [holidays, setHolidays] = useState([]);
    const [isHolidaysLoading, setIsHolidaysLoading] = useState(false);
    const [holidaysError, setHolidaysError] = useState(null);

    const loadSettings = () => {
        setIsLoading(true);
        setError(null);
        getFetchRequest(
            makeUrl([BUSINESS_CALENDAR_URL]),
            (data) => {
                setCalendarCode((data && data.calendarCode) || CALENDAR_RU);
                setValidUntil((data && data.validUntil) || null);
                setYears(Array.isArray(data && data.years) ? data.years : []);
                setIsLoading(false);
                const activeCode = (data && data.calendarCode) || CALENDAR_RU;
                loadHolidays(activeCode);
            },
            (err) => {
                console.error('Ошибка загрузки настроек календаря:', err);
                setError('Не удалось загрузить настройки календаря');
                setIsLoading(false);
            }
        );
    };

    const loadHolidays = (code) => {
        const targetCode = (code || calendarCode || CALENDAR_RU);
        setIsHolidaysLoading(true);
        setHolidaysError(null);
        getFetchRequest(
            makeUrl([BUSINESS_CALENDAR_URL, 'holidays/', `?calendarCode=${encodeURIComponent(targetCode)}`]),
            (data) => {
                const list = Array.isArray(data && data.holidays) ? data.holidays : [];
                setHolidays(list);
                setIsHolidaysLoading(false);
            },
            (err) => {
                console.error('Ошибка загрузки списка праздников:', err);
                setHolidays([]);
                setIsHolidaysLoading(false);
                setHolidaysError('Не удалось загрузить список праздников.');
            }
        );
    };

    useEffect(() => {
        loadSettings();
    }, []);

    const handleCalendarChange = (code) => {
        if (!code || code === calendarCode) return;
        setIsLoading(true);
        setError(null);
        postFetchRequest(
            makeUrl([BUSINESS_CALENDAR_URL]),
            { calendarCode: code },
            (data) => {
                setCalendarCode((data && data.calendarCode) || code);
                setValidUntil((data && data.validUntil) || null);
                setYears(Array.isArray(data && data.years) ? data.years : []);
                setIsLoading(false);
                const activeCode = (data && data.calendarCode) || code;
                loadHolidays(activeCode);
            },
            (err) => {
                console.error('Ошибка смены календаря:', err);
                errorCallback(err);
                setIsLoading(false);
            }
        );
    };

    const handleRefresh = () => {
        setIsRefreshing(true);
        setError(null);
        postFetchRequest(
            makeUrl([BUSINESS_CALENDAR_URL, 'refresh/']),
            {},
            (data) => {
                setCalendarCode((data && data.calendarCode) || calendarCode);
                setValidUntil((data && data.validUntil) || null);
                setYears(Array.isArray(data && data.years) ? data.years : []);
                setIsRefreshing(false);
                const activeCode = (data && data.calendarCode) || calendarCode;
                loadHolidays(activeCode);
            },
            (err) => {
                console.error('Ошибка обновления календаря:', err);
                errorCallback(err);
                setIsRefreshing(false);
            }
        );
    };

    const handleTestCalculate = (event) => {
        if (event && event.preventDefault) {
            event.preventDefault();
        }
        setTestError(null);
        setTestResult(null);

        if (!testBaseDate) {
            setTestError('Укажите дату начала.');
            return;
        }

        const daysInt = parseInt(testDays, 10);
        if (Number.isNaN(daysInt) || daysInt < 0) {
            setTestError('Количество рабочих дней должно быть неотрицательным числом.');
            return;
        }

        setIsTestLoading(true);
        postFetchRequest(
            makeUrl([BUSINESS_CALENDAR_URL, 'calculate/']),
            {
                baseDate: testBaseDate,
                days: daysInt,
                calendarCode,
            },
            (data) => {
                setIsTestLoading(false);
                if (!data || !data.targetDate) {
                    setTestError('Не удалось рассчитать дату по указанным параметрам.');
                    return;
                }
                setTestResult({
                    targetDate: data.targetDate,
                    baseDate: data.baseDate || testBaseDate,
                    days: data.days ?? daysInt,
                    calendarCode: data.calendarCode || calendarCode,
                });
            },
            (err) => {
                console.error('Ошибка расчёта даты по календарю:', err);
                errorCallback(err);
                setIsTestLoading(false);
                setTestError('Ошибка при расчёте даты. Попробуйте ещё раз.');
            }
        );
    };

    const workspaceClass =
        isCollapsed === null
            ? ''
            : isCollapsed
            ? 'workspace_collapsed'
            : 'workspace_full-width';

    const isDataExpired = (() => {
        if (!validUntil) {
            return true;
        }
        try {
            const today = new Date();
            const maxDay = new Date(validUntil);
            if (Number.isNaN(maxDay.getTime())) {
                return false;
            }
            // если максимальная дата в календаре раньше сегодняшнего дня — считаем данные устаревшими
            return maxDay < new Date(today.getFullYear(), today.getMonth(), today.getDate());
        } catch {
            return false;
        }
    })();

    return (
        <section className={`workspace ${workspaceClass} calendar-settings__page`}>
            <h1 className="calendar-settings__title">Производственный календарь</h1>

            {error && (
                <div className="calendar-settings__error">
                    {error}
                </div>
            )}

            {isLoading && (
                <div className="calendar-settings__loading">Загрузка настроек...</div>
            )}

            <div className="calendar-settings__grid">
                <div className="calendar-settings__column">
                    <div className="calendar-settings__section">
                        <h2 className="calendar-settings__subtitle">Активный календарь</h2>
                        <p className="calendar-settings__description">
                            Выберите, по какому производственному календарю считать рабочие дни
                            для заказов клиентов и заказов поставщиков.
                        </p>
                        <div className="calendar-settings__options">
                            <label className="calendar-settings__option">
                                <input
                                    type="radio"
                                    name="business-calendar"
                                    value={CALENDAR_RU}
                                    checked={calendarCode === CALENDAR_RU}
                                    onChange={() => handleCalendarChange(CALENDAR_RU)}
                                    disabled={isLoading || isRefreshing}
                                />
                                <span className="calendar-settings__option-label">
                                    <span className="calendar-settings__option-title">Россия</span>
                                    <span className="calendar-settings__option-text">
                                        Используется для расчёта сроков поставки по российскому календарю.
                                    </span>
                                </span>
                            </label>
                            <label className="calendar-settings__option">
                                <input
                                    type="radio"
                                    name="business-calendar"
                                    value={CALENDAR_CN}
                                    checked={calendarCode === CALENDAR_CN}
                                    onChange={() => handleCalendarChange(CALENDAR_CN)}
                                    disabled={isLoading || isRefreshing}
                                />
                                <span className="calendar-settings__option-label">
                                    <span className="calendar-settings__option-title">Китай</span>
                                    <span className="calendar-settings__option-text">
                                        Используется для расчёта сроков поставки по китайскому календарю.
                                    </span>
                                </span>
                            </label>
                        </div>
                    </div>

                    <div className="calendar-settings__section">
                        <h2 className="calendar-settings__subtitle">Праздники по активному календарю</h2>
                        <p className="calendar-settings__hint">
                            Ниже приведены праздничные дни и отработки,
                            загруженные для активного календаря ({calendarCode === CALENDAR_RU ? 'Россия' : 'Китай'}).
                        </p>
                        {isHolidaysLoading && (
                            <div className="calendar-settings__loading">
                                Загрузка списка праздников...
                            </div>
                        )}
                        {holidaysError && (
                            <div className="calendar-settings__tester-error">
                                {holidaysError}
                            </div>
                        )}
                        {!isHolidaysLoading && !holidaysError && holidays && holidays.length > 0 && (
                            <ul className="calendar-settings__holiday-list">
                                {holidays.map((h, index) => (
                                    <li key={`${h.date}-${index}`} className="calendar-settings__holiday-item">
                                        <span className="calendar-settings__holiday-date">
                                            {formatDateRu(h.date)}
                                        </span>
                                        <span className="calendar-settings__holiday-name">
                                            {h.name || (h.isHoliday ? 'Праздничный день' : 'День отработки')}
                                        </span>
                                        {h.isWorkdayOverride && !h.isHoliday && (
                                            <span className="calendar-settings__holiday-badge calendar-settings__holiday-badge--work">
                                                отработка
                                            </span>
                                        )}
                                        {h.isHoliday && (
                                            <span className="calendar-settings__holiday-badge calendar-settings__holiday-badge--holiday">
                                                праздник
                                            </span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                        {!isHolidaysLoading && !holidaysError && (!holidays || holidays.length === 0) && (
                            <p className="calendar-settings__info">
                                Для выбранного календаря пока нет загруженных праздничных дней.
                            </p>
                        )}
                    </div>

                    <div className="calendar-settings__section">
                        <h2 className="calendar-settings__subtitle">Обновление календаря</h2>
                        <p className="calendar-settings__hint">
                            При обновлении будут удалены данные старше текущего года,
                            а календарь для текущего и, при наличии данных, следующего года
                            будет загружен заново из внешнего сервиса ({calendarCode === CALENDAR_RU ? 'Россия' : 'Китай'}).
                        </p>
                        <button
                            type="button"
                            className="order-button calendar-settings__refresh-button"
                            onClick={handleRefresh}
                            disabled={isRefreshing || isLoading}
                        >
                            {isRefreshing ? 'Обновление…' : 'Обновить календарь на год'}
                        </button>
                    </div>
                </div>

                <div className="calendar-settings__column">
                    <div className="calendar-settings__section">
                        <h2 className="calendar-settings__subtitle">Актуальность данных</h2>
                        <p className="calendar-settings__info">
                            Календарь для{' '}
                            <strong>{calendarCode === CALENDAR_RU ? 'России' : 'Китая'}</strong>{' '}
                            актуален до:{' '}
                            <strong>{formatDateRu(validUntil)}</strong>
                        </p>
                        {years && years.length > 0 && (
                            <p className="calendar-settings__info">
                                Загружены годы: {years.join(', ')}
                            </p>
                        )}

                        <div className="calendar-settings__status">
                            <span
                                className={`calendar-settings__status-dot ${
                                    isDataExpired ? 'calendar-settings__status-dot--danger' : 'calendar-settings__status-dot--ok'
                                }`}
                            />
                            <span className="calendar-settings__status-text">
                                {isDataExpired
                                    ? 'Внимание: календарь не содержит данных на текущую дату. Рекомендуется обновить календарь.'
                                    : 'Данные календаря покрывают текущий год. Расчёт рабочих дней выполняется по загруженному календарю.'}
                            </span>
                        </div>
                    </div>

                    <div className="calendar-settings__section">
                        <h2 className="calendar-settings__subtitle">Тест расчёта рабочих дней</h2>
                        <p className="calendar-settings__hint">
                            Укажите дату начала и количество рабочих дней, чтобы проверить,
                            как сервис рассчитает дату по активному календарю ({calendarCode === CALENDAR_RU ? 'Россия' : 'Китай'}).
                        </p>
                        <form className="calendar-settings__tester-form" onSubmit={handleTestCalculate}>
                            <div className="calendar-settings__tester-row">
                                <label className="calendar-settings__tester-label">
                                    Дата начала
                                    <input
                                        type="date"
                                        className="calendar-settings__tester-input"
                                        value={testBaseDate}
                                        onChange={(e) => setTestBaseDate(e.target.value)}
                                    />
                                </label>
                                <label className="calendar-settings__tester-label">
                                    Рабочие дни
                                    <input
                                        type="number"
                                        min="0"
                                        className="calendar-settings__tester-input"
                                        value={testDays}
                                        onChange={(e) => setTestDays(e.target.value)}
                                        placeholder="0"
                                    />
                                </label>
                            </div>
                            {testError && (
                                <div className="calendar-settings__tester-error">
                                    {testError}
                                </div>
                            )}
                            {testResult && (
                                <div className="calendar-settings__tester-result">
                                    <span className="calendar-settings__tester-result-label">Рассчитанная дата:</span>
                                    <span className="calendar-settings__tester-result-value">
                                        <strong>{formatDateRu(testResult.targetDate)}</strong>
                                    </span>
                                    <span className="calendar-settings__tester-result-note">
                                        (от {formatDateRu(testResult.baseDate)} + {testResult.days} раб. дн.,
                                        календарь: {testResult.calendarCode === CALENDAR_RU ? 'Россия' : 'Китай'})
                                    </span>
                                </div>
                            )}
                            <button
                                type="submit"
                                className="order-button calendar-settings__tester-button"
                                disabled={isTestLoading}
                            >
                                {isTestLoading ? 'Расчёт…' : 'Рассчитать дату'}
                            </button>
                        </form>
                    </div>

                </div>
            </div>
        </section>
    );
};

export default BusinessCalendarTab;

